import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseInfiniteQueryOptions
} from "react-query";

import { APIFetchV3, RequestHeaders } from "@certa/network";
import type {
  UseMutationExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";

import { queryClient } from "../../utils/utils";

type MassDelegationPayload = {
  user_email: string;
  delegation_id?: number;
  start_date: string;
  end_date: string;
  name: string;
  reason?: string;
  delegate_future_tasks?: boolean;
  delegate_in_progress_tasks?: boolean;
};

const createMassDelegation = async (payload: MassDelegationPayload) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  const endpoint = "mass-delegation/";
  return APIFetchV3<MassDelegationResponse>(endpoint, requestOptions).then(
    createMassDelegationModelCreator
  );
};

type MassDelegationResponse = {
  id: number;
  name: string;
  reason: string;
  is_active: boolean;
  user: {
    id: number;
    email: string;
    name: string;
  };
  creator: {
    id: number;
    email: string;
    name: string;
  };
  start_date: string;
  end_date: string;
  delegate_future_tasks: boolean;
  delegate_in_progress_tasks: boolean;
  status: "active" | "scheduled" | "completed" | "cancelled";
};

export type DelegationRequest = ReturnType<
  typeof createMassDelegationModelCreator
>;

const createMassDelegationModelCreator = (response: MassDelegationResponse) => {
  return {
    id: response.id,
    name: response.name,
    reason: response.reason,
    isActive: response.is_active,
    user: response.user,
    creator: response.creator,
    startDate: response.start_date,
    endDate: response.end_date,
    delegateFutureTasks: response.delegate_future_tasks,
    delegateInProgressTasks: response.delegate_in_progress_tasks,
    status: response.status
  };
};

export const useCreateMassDelegation = (
  extraConfig?: UseMutationExtraConfigType
) => {
  return useMutation(createMassDelegation, {
    ...extraConfig,
    onSuccess: (response, payload, context) => {
      queryClient.invalidateQueries(["delegation-requests"]);
      extraConfig?.onSuccess?.(response, payload, context);
    }
  });
};

const updateMassDelegation = async (payload: MassDelegationPayload) => {
  const requestOptions: RequestInit = {
    method: "PUT",
    headers: RequestHeaders.PUT,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  const endpoint = "mass-delegation/";
  return APIFetchV3<MassDelegationResponse>(endpoint, requestOptions).then(
    createMassDelegationModelCreator
  );
};

export const useUpdateMassDelegation = (
  extraConfig?: UseMutationExtraConfigType
) => {
  return useMutation(updateMassDelegation, {
    ...extraConfig,
    onSuccess: (response, payload, context) => {
      queryClient.invalidateQueries({
        queryKey: ["delegation-requests"]
      });
      extraConfig?.onSuccess?.(response, payload, context);
    }
  });
};

const deleteMassDelegation = async (id: number) => {
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.DELETE,
    credentials: "include",
    body: JSON.stringify({ delegation_id: id })
  };

  const endpoint = "mass-delegation/";
  return APIFetchV3<null>(endpoint, requestOptions).then(response => response);
};

export const useDeleteMassDelegation = (
  extraConfig?: UseMutationExtraConfigType
) => {
  return useMutation(deleteMassDelegation, {
    ...extraConfig,
    onSuccess: (response, payload, context) => {
      queryClient.invalidateQueries({
        queryKey: ["delegation-requests"]
      });
      extraConfig?.onSuccess?.(response, payload, context);
    }
  });
};

type GetDelegationRequestResponse = {
  count: number;
  results: MassDelegationResponse[];
};

const getDelegationRequestResponseCreator = (
  response: GetDelegationRequestResponse
) => {
  return {
    count: response.count,
    results: response.results.map(createMassDelegationModelCreator)
  };
};

type GetDelegationRequestsParams = {
  page: number;
  page_size: number;
};

const getDelegationRequests = async (params: GetDelegationRequestsParams) => {
  const requestOptions: RequestInit = {
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      queryParams.append(key, value.toString());
    }
  });

  const endpoint = `delegation-requests/?${queryParams.toString()}`;
  return APIFetchV3<GetDelegationRequestResponse>(
    endpoint,
    requestOptions
  ).then(getDelegationRequestResponseCreator);
};

type UseGetDelegationRequestsParams = {
  page: number;
  page_size: number;
  status?: string;
  user_email?: string;
  search?: string;
};

export const useGetDelegationRequests = (
  params: UseGetDelegationRequestsParams,
  config?: UseQueryOptions<UseQueryReturnType<typeof getDelegationRequests>>
) => {
  return useQuery({
    ...config,
    queryKey: ["delegation-requests", ...Object.values(params)] as QueryKey,
    queryFn: () => getDelegationRequests(params)
  });
};

type GetDelegateesResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    profile: {
      business_unit_full_access: { id?: number; name?: string }[];
      business_unit_read_access: { id?: number; name?: string }[];
      region_full_access: { id?: number; name?: string }[];
      region_read_access: { id?: number; name?: string }[];
    };
    groups: {
      id?: number;
      name?: string;
    }[];
  }[];
};

export type Delegatee = ReturnType<typeof getDelegateesModelCreator>;

const getDelegateesModelCreator = (response: GetDelegateesResponse) => {
  return {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(delegatee => ({
      id: delegatee.id,
      email: delegatee.email,
      firstName: delegatee.first_name,
      lastName: delegatee.last_name,
      profile: {
        businessUnitFullAccess: delegatee.profile.business_unit_full_access,
        businessUnitReadAccess: delegatee.profile.business_unit_read_access,
        regionFullAccess: delegatee.profile.region_full_access,
        regionReadAccess: delegatee.profile.region_read_access
      },
      groups: delegatee.groups
    }))
  };
};

type GetDelegateesParams = {
  page: number;
  search: string;
};

const getDelegatees = async (params: GetDelegateesParams) => {
  const { page = 1, search } = params;

  const requestOptions: RequestInit = {
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  let endpoint = `delegatees/?page=${page}&page_size=20`;

  if (search) {
    endpoint = `delegatees/?page=${page}&page_size=20&email=${search}`;
  }

  return APIFetchV3<GetDelegateesResponse>(endpoint, requestOptions).then(
    getDelegateesModelCreator
  );
};

type UseGetDelegateesParams = {
  search: string;
};

export const useGetDelegatees = (
  params: UseGetDelegateesParams,
  config?: Omit<
    UseInfiniteQueryOptions<ReturnType<typeof getDelegateesModelCreator>>,
    "queryKey" | "queryFn"
  >
) => {
  return useInfiniteQuery({
    ...config,
    queryKey: ["delegatees", params.search] as QueryKey,
    queryFn: ({ pageParam }) =>
      getDelegatees({ page: pageParam, search: params.search }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    getNextPageParam: lastPage => {
      if (lastPage.next) {
        const url = new URL(lastPage.next);
        const urlParams = new URLSearchParams(url.search);
        return urlParams.get("page");
      }
      return false;
    }
  });
};
